import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Tab,
  Tabs,
  SelectChangeEvent,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import HistoryIcon from '@mui/icons-material/History';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, ReferenceLine, Legend,
} from 'recharts';
import CorrelationHeatmap from '../components/CorrelationHeatmap';
import CorrelationComparisonTable from '../components/CorrelationComparisonTable';
import {
  getCorrelationMatrix,
  getCorrelationSectors,
  getCorrelationSectorSymbols,
  getCorrelationBenchmarks,
  searchCorrelationSymbols,
  getModelPortfolios,
  getPortfolioCorrelationMatrix,
  getPortfolioCorrelationHistory,
  CorrelationMatrixResponse,
  CorrelationSearchResult,
  CorrelationSectorSymbol,
  BenchmarkSymbol,
  ModelPortfolioSummary,
  PortfolioCorrelationMatrixResponse,
  PortfolioCorrelationHistoryResponse,
} from '../services/api';

const TIMEFRAMES = [
  { value: '10D', label: '10D' },
  { value: '20D', label: '20D' },
  { value: '30D', label: '30D' },
  { value: '1M', label: '1M' },
  { value: '3M', label: '3M' },
  { value: '6M', label: '6M' },
  { value: '1Y', label: '1Y' },
];

const COUNTRIES = [
  { code: 'US', name: 'United States' },
  { code: 'UK', name: 'United Kingdom' },
  { code: 'FR', name: 'France' },
  { code: 'DE', name: 'Germany' },
  { code: 'ES', name: 'Spain' },
  { code: 'NL', name: 'Netherlands' },
  { code: 'BE', name: 'Belgium' },
];

const CORRELATION_STORAGE_KEY = 'correlation_study_symbols';

const CorrelationStudies: React.FC = () => {
  // Tab state (0 = Custom Tickers, 1 = By Sector, 2 = Macro, 3 = Model Portfolios)
  const [tabIndex, setTabIndex] = useState(0);

  // Shared state
  const [timeframe, setTimeframe] = useState('1M');
  const [correlationData, setCorrelationData] = useState<CorrelationMatrixResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Model Portfolios (intra-portfolio) tab state ──
  const [allPortfolios, setAllPortfolios] = useState<ModelPortfolioSummary[]>([]);
  const [portfolioMatrixTimeframe, setPortfolioMatrixTimeframe] = useState('1Y');
  const [portfolioMatrix, setPortfolioMatrix] = useState<PortfolioCorrelationMatrixResponse | null>(null);
  const [portfolioMatrixLoading, setPortfolioMatrixLoading] = useState(false);
  const [portfolioError, setPortfolioError] = useState<string | null>(null);

  const [pairA, setPairA] = useState<number | ''>('');
  const [pairB, setPairB] = useState<number | ''>('');
  const [lookbackPeriod, setLookbackPeriod] = useState('1M');
  const [historyDays, setHistoryDays] = useState(180);
  const [stepDays, setStepDays] = useState(7);
  const [pairHistory, setPairHistory] = useState<PortfolioCorrelationHistoryResponse | null>(null);
  const [pairHistoryLoading, setPairHistoryLoading] = useState(false);

  // Historical mode state
  const [historicalMode, setHistoricalMode] = useState(false);
  const [asOfDate, setAsOfDate] = useState<string>(''); // YYYY-MM-DD format

  // Custom tickers state
  const [symbols, setSymbols] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CorrelationSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Sector mode state
  const [sectors, setSectors] = useState<string[]>([]);
  const [selectedSector, setSelectedSector] = useState<string>('');
  const [selectedCountries, setSelectedCountries] = useState<string[]>(['US']);
  const [sectorSymbols, setSectorSymbols] = useState<CorrelationSectorSymbol[]>([]);
  const [sectorLoading, setSectorLoading] = useState(false);

  // Benchmarks state
  const [availableBenchmarks, setAvailableBenchmarks] = useState<BenchmarkSymbol[]>([]);
  const [sectorBenchmarks, setSectorBenchmarks] = useState<string[]>([]);

  // Macro mode state
  const [macroSymbols, setMacroSymbols] = useState<string[]>([]);
  const [macroCategoryFilter, setMacroCategoryFilter] = useState<string>('');

  // Load saved symbols on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(CORRELATION_STORAGE_KEY);
      if (saved) {
        setSymbols(JSON.parse(saved));
      }
    } catch (e) {
      console.error('Error loading saved symbols:', e);
    }
  }, []);

  // Save symbols to localStorage
  useEffect(() => {
    localStorage.setItem(CORRELATION_STORAGE_KEY, JSON.stringify(symbols));
  }, [symbols]);

  // Fetch available sectors on mount
  useEffect(() => {
    const fetchSectors = async () => {
      try {
        const result = await getCorrelationSectors();
        setSectors(result.sectors);
      } catch (e) {
        console.error('Error fetching sectors:', e);
      }
    };
    fetchSectors();
  }, []);

  // Search for symbols with debounce
  useEffect(() => {
    if (searchQuery.length < 1) {
      setSearchResults([]);
      return;
    }

    const searchTimeout = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const result = await searchCorrelationSymbols(searchQuery, 10);
        setSearchResults(result.results);
      } catch (e) {
        console.error('Error searching symbols:', e);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [searchQuery]);

  // Fetch sector symbols when sector or countries change
  useEffect(() => {
    if (!selectedSector) {
      setSectorSymbols([]);
      return;
    }

    const fetchSectorSymbols = async () => {
      setSectorLoading(true);
      try {
        const result = await getCorrelationSectorSymbols(
          selectedSector,
          selectedCountries,
          20
        );
        setSectorSymbols(result.symbols);
      } catch (e) {
        console.error('Error fetching sector symbols:', e);
      } finally {
        setSectorLoading(false);
      }
    };

    fetchSectorSymbols();
  }, [selectedSector, selectedCountries]);

  // Fetch available benchmarks (indices + ETFs) on mount
  useEffect(() => {
    const fetchBenchmarks = async () => {
      try {
        const result = await getCorrelationBenchmarks();
        setAvailableBenchmarks(result.benchmarks);
      } catch (e) {
        console.error('Error fetching benchmarks:', e);
      }
    };
    fetchBenchmarks();
  }, []);

  // Calculate correlation matrix
  const calculateCorrelation = useCallback(
    async (symbolsToUse: string[]) => {
      if (symbolsToUse.length < 2) {
        setError('At least 2 symbols required for correlation analysis');
        setCorrelationData(null);
        return;
      }

      // Validate historical date if in historical mode
      if (historicalMode && !asOfDate) {
        setError('Please select an "As of" date for historical analysis');
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const dateToUse = historicalMode && asOfDate ? asOfDate : undefined;
        const result = await getCorrelationMatrix(symbolsToUse, timeframe, dateToUse);
        setCorrelationData(result);

        if (result.missing_symbols.length > 0) {
          setError(`No price data available for: ${result.missing_symbols.join(', ')}`);
        }
      } catch (e: any) {
        console.error('Error calculating correlation:', e);
        setError(
          e.response?.data?.detail || e.message || 'Failed to calculate correlation'
        );
        setCorrelationData(null);
      } finally {
        setLoading(false);
      }
    },
    [timeframe, historicalMode, asOfDate]
  );

  // Handle add symbol
  const handleAddSymbol = (symbol: string) => {
    const upperSymbol = symbol.toUpperCase().trim();
    if (upperSymbol && !symbols.includes(upperSymbol)) {
      setSymbols([...symbols, upperSymbol]);
    }
    setSearchQuery('');
    setSearchResults([]);
  };

  // Handle remove symbol
  const handleRemoveSymbol = (symbol: string) => {
    setSymbols(symbols.filter((s) => s !== symbol));
  };

  // Handle clear all symbols
  const handleClearSymbols = () => {
    setSymbols([]);
    setCorrelationData(null);
  };

  // Handle calculate for custom tickers
  const handleCalculateCustom = () => {
    calculateCorrelation(symbols);
  };

  // Handle calculate for sector
  const handleCalculateSector = () => {
    const sectorSymbolList = sectorSymbols.map((s) => s.symbol);
    const combined = [...sectorSymbolList, ...sectorBenchmarks.filter((b) => !sectorSymbolList.includes(b))];
    calculateCorrelation(combined);
  };

  // Handle calculate for macro
  const handleCalculateMacro = () => {
    calculateCorrelation(macroSymbols);
  };

  // ── Model Portfolios (intra-portfolio) tab ──
  useEffect(() => {
    if (tabIndex === 3 && allPortfolios.length === 0) {
      getModelPortfolios().then(setAllPortfolios).catch(() => {});
    }
  }, [tabIndex, allPortfolios.length]);

  const handleCalculatePortfolioMatrix = useCallback(() => {
    setPortfolioMatrixLoading(true);
    setPortfolioError(null);
    getPortfolioCorrelationMatrix(undefined, portfolioMatrixTimeframe)
      .then(setPortfolioMatrix)
      .catch(() => setPortfolioError('Failed to calculate the portfolio correlation matrix.'))
      .finally(() => setPortfolioMatrixLoading(false));
  }, [portfolioMatrixTimeframe]);

  const handleCalculatePairHistory = useCallback(() => {
    if (!pairA || !pairB || pairA === pairB) return;
    setPairHistoryLoading(true);
    setPortfolioError(null);
    getPortfolioCorrelationHistory(Number(pairA), Number(pairB), lookbackPeriod, historyDays, stepDays)
      .then((r) => {
        if (r.error) setPortfolioError(r.error);
        setPairHistory(r);
      })
      .catch(() => setPortfolioError('Failed to calculate rolling correlation for this pair.'))
      .finally(() => setPairHistoryLoading(false));
  }, [pairA, pairB, lookbackPeriod, historyDays, stepDays]);

  // Handle country selection change
  const handleCountryChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setSelectedCountries(typeof value === 'string' ? value.split(',') : value);
  };

  // Group benchmarks by category for display
  const benchmarksByCategory = availableBenchmarks.reduce<Record<string, BenchmarkSymbol[]>>((acc, b) => {
    const cat = b.category;
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(b);
    return acc;
  }, {});

  const renderBenchmarkSelect = (
    selected: string[],
    onChange: (value: string[]) => void,
  ) => (
    <FormControl size="small" sx={{ minWidth: 280 }}>
      <InputLabel>Include Indices / ETFs</InputLabel>
      <Select
        multiple
        value={selected}
        onChange={(e) => {
          const val = e.target.value;
          onChange(typeof val === 'string' ? val.split(',') : val);
        }}
        input={<OutlinedInput label="Include Indices / ETFs" />}
        renderValue={(sel) => sel.join(', ')}
        MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
      >
        {Object.entries(benchmarksByCategory).flatMap(([category, items]) => [
          <MenuItem key={`cat-${category}`} disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 0.5 }}>
            {category}
          </MenuItem>,
          ...items.map((b) => (
            <MenuItem key={b.symbol} value={b.symbol} sx={{ pl: 2 }}>
              <Checkbox checked={selected.indexOf(b.symbol) > -1} size="small" />
              <ListItemText
                primary={b.symbol}
                secondary={b.name}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption', noWrap: true, sx: { maxWidth: 220 } }}
              />
            </MenuItem>
          )),
        ])}
      </Select>
    </FormControl>
  );

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a1a2e' }}>
          Correlation Studies
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Analyze correlations between stocks using historical price data
        </Typography>
      </Box>

      {/* Input Mode Tabs */}
      <Paper sx={{ mb: 3 }} elevation={2}>
        <Tabs
          value={tabIndex}
          onChange={(_, newValue) => setTabIndex(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Custom Tickers" />
          <Tab label="By Sector" />
          <Tab label="Macro" />
          <Tab label="Model Portfolios" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Custom Tickers Tab */}
          {tabIndex === 0 && (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-start',
                  mb: 2,
                  flexWrap: 'wrap',
                }}
              >
                <Autocomplete
                  freeSolo
                  options={searchResults}
                  getOptionLabel={(option) =>
                    typeof option === 'string'
                      ? option
                      : `${option.symbol} - ${option.name}`
                  }
                  inputValue={searchQuery}
                  onInputChange={(_, value) => setSearchQuery(value)}
                  onChange={(_, value) => {
                    if (value) {
                      const symbol = typeof value === 'string' ? value : value.symbol;
                      handleAddSymbol(symbol);
                    }
                  }}
                  loading={searchLoading}
                  sx={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder="Search ticker or company name"
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {searchLoading ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.symbol}>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {option.symbol}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.name} {option.country && `(${option.country})`}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />

                <ToggleButtonGroup
                  value={timeframe}
                  exclusive
                  onChange={(_, value) => value && setTimeframe(value)}
                  size="small"
                >
                  {TIMEFRAMES.map((tf) => (
                    <ToggleButton key={tf.value} value={tf.value}>
                      {tf.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                {/* Historical Mode Toggle */}
                <Tooltip title="View correlation as of a specific past date">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={historicalMode}
                        onChange={(e) => {
                          setHistoricalMode(e.target.checked);
                          if (!e.target.checked) {
                            setAsOfDate('');
                          }
                        }}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <HistoryIcon fontSize="small" />
                        <Typography variant="body2">Historical</Typography>
                      </Box>
                    }
                  />
                </Tooltip>

                {historicalMode && (
                  <TextField
                    type="date"
                    size="small"
                    label="As of Date"
                    value={asOfDate}
                    onChange={(e) => setAsOfDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: new Date().toISOString().split('T')[0],
                    }}
                    sx={{ width: 160 }}
                  />
                )}
              </Box>

              {/* Selected Symbols */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {symbols.map((symbol) => (
                  <Chip
                    key={symbol}
                    label={symbol}
                    onDelete={() => handleRemoveSymbol(symbol)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
                {symbols.length > 0 && (
                  <Chip
                    label="Clear All"
                    onClick={handleClearSymbols}
                    color="secondary"
                    variant="outlined"
                    icon={<ClearIcon />}
                  />
                )}
              </Box>

              <Button
                variant="contained"
                onClick={handleCalculateCustom}
                disabled={symbols.length < 2 || loading}
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />
                }
              >
                Calculate Correlation
              </Button>
            </Box>
          )}

          {/* Sector Tab */}
          {tabIndex === 1 && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Sector</InputLabel>
                  <Select
                    value={selectedSector}
                    onChange={(e) => setSelectedSector(e.target.value)}
                    label="Sector"
                  >
                    {sectors.map((sector) => (
                      <MenuItem key={sector} value={sector}>
                        {sector}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Countries</InputLabel>
                  <Select
                    multiple
                    value={selectedCountries}
                    onChange={handleCountryChange}
                    input={<OutlinedInput label="Countries" />}
                    renderValue={(selected) => selected.join(', ')}
                  >
                    {COUNTRIES.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        <Checkbox
                          checked={selectedCountries.indexOf(country.code) > -1}
                          size="small"
                        />
                        <ListItemText primary={country.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <ToggleButtonGroup
                  value={timeframe}
                  exclusive
                  onChange={(_, value) => value && setTimeframe(value)}
                  size="small"
                >
                  {TIMEFRAMES.map((tf) => (
                    <ToggleButton key={tf.value} value={tf.value}>
                      {tf.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                {/* Historical Mode Toggle */}
                <Tooltip title="View correlation as of a specific past date">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={historicalMode}
                        onChange={(e) => {
                          setHistoricalMode(e.target.checked);
                          if (!e.target.checked) {
                            setAsOfDate('');
                          }
                        }}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <HistoryIcon fontSize="small" />
                        <Typography variant="body2">Historical</Typography>
                      </Box>
                    }
                  />
                </Tooltip>

                {historicalMode && (
                  <TextField
                    type="date"
                    size="small"
                    label="As of Date"
                    value={asOfDate}
                    onChange={(e) => setAsOfDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: new Date().toISOString().split('T')[0],
                    }}
                    sx={{ width: 160 }}
                  />
                )}
                {renderBenchmarkSelect(sectorBenchmarks, setSectorBenchmarks)}
              </Box>

              {/* Selected Benchmarks Preview */}
              {sectorBenchmarks.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {sectorBenchmarks.map((sym) => {
                    const b = availableBenchmarks.find((x) => x.symbol === sym);
                    return (
                      <Chip
                        key={sym}
                        label={`${sym}${b ? ` - ${b.name}` : ''}`}
                        size="small"
                        color="info"
                        variant="outlined"
                        onDelete={() => setSectorBenchmarks(sectorBenchmarks.filter((s) => s !== sym))}
                      />
                    );
                  })}
                </Box>
              )}

              {/* Sector Symbols Preview */}
              {sectorLoading ? (
                <Box sx={{ py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : sectorSymbols.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Top {sectorSymbols.length} stocks in {selectedSector}:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {sectorSymbols.map((s) => (
                      <Chip key={s.symbol} label={s.symbol} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              ) : selectedSector ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No stocks found for selected criteria
                </Typography>
              ) : null}

              <Button
                variant="contained"
                onClick={handleCalculateSector}
                disabled={sectorSymbols.length < 2 || loading}
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />
                }
              >
                Calculate Sector Correlation
              </Button>
            </Box>
          )}

          {/* Macro Tab */}
          {tabIndex === 2 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Correlate market indices, ETFs, bonds, and commodities against each other for broad macro analysis.
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <InputLabel>Filter by Category</InputLabel>
                  <Select
                    value={macroCategoryFilter}
                    onChange={(e) => setMacroCategoryFilter(e.target.value)}
                    label="Filter by Category"
                  >
                    <MenuItem value="">
                      <em>All Categories</em>
                    </MenuItem>
                    {Object.keys(benchmarksByCategory).sort().map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat} ({benchmarksByCategory[cat].length})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: 350 }}>
                  <InputLabel>Select Instruments</InputLabel>
                  <Select
                    multiple
                    value={macroSymbols}
                    onChange={(e) => {
                      const val = e.target.value;
                      setMacroSymbols(typeof val === 'string' ? val.split(',') : val);
                    }}
                    input={<OutlinedInput label="Select Instruments" />}
                    renderValue={(sel) => `${sel.length} selected`}
                    MenuProps={{ PaperProps: { style: { maxHeight: 400 } } }}
                  >
                    {Object.entries(benchmarksByCategory)
                      .filter(([cat]) => !macroCategoryFilter || cat === macroCategoryFilter)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .flatMap(([category, items]) => [
                        <MenuItem key={`mcat-${category}`} disabled sx={{ opacity: 1, fontWeight: 600, fontSize: '0.8rem', color: 'text.secondary', py: 0.5 }}>
                          {category}
                        </MenuItem>,
                        ...items.map((b) => (
                          <MenuItem key={b.symbol} value={b.symbol} sx={{ pl: 2 }}>
                            <Checkbox checked={macroSymbols.indexOf(b.symbol) > -1} size="small" />
                            <ListItemText
                              primary={b.symbol}
                              secondary={b.name}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                              secondaryTypographyProps={{ variant: 'caption', noWrap: true, sx: { maxWidth: 260 } }}
                            />
                          </MenuItem>
                        )),
                      ])}
                  </Select>
                </FormControl>

                <ToggleButtonGroup
                  value={timeframe}
                  exclusive
                  onChange={(_, value) => value && setTimeframe(value)}
                  size="small"
                >
                  {TIMEFRAMES.map((tf) => (
                    <ToggleButton key={tf.value} value={tf.value}>
                      {tf.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                {/* Historical Mode Toggle */}
                <Tooltip title="View correlation as of a specific past date">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={historicalMode}
                        onChange={(e) => {
                          setHistoricalMode(e.target.checked);
                          if (!e.target.checked) {
                            setAsOfDate('');
                          }
                        }}
                        size="small"
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <HistoryIcon fontSize="small" />
                        <Typography variant="body2">Historical</Typography>
                      </Box>
                    }
                  />
                </Tooltip>

                {historicalMode && (
                  <TextField
                    type="date"
                    size="small"
                    label="As of Date"
                    value={asOfDate}
                    onChange={(e) => setAsOfDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: new Date().toISOString().split('T')[0],
                    }}
                    sx={{ width: 160 }}
                  />
                )}
              </Box>

              {/* Selected Macro Instruments */}
              {macroSymbols.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                  {macroSymbols.map((sym) => {
                    const b = availableBenchmarks.find((x) => x.symbol === sym);
                    return (
                      <Chip
                        key={sym}
                        label={`${sym}${b ? ` - ${b.name}` : ''}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                        onDelete={() => setMacroSymbols(macroSymbols.filter((s) => s !== sym))}
                      />
                    );
                  })}
                  <Chip
                    label="Clear All"
                    size="small"
                    color="secondary"
                    variant="outlined"
                    icon={<ClearIcon />}
                    onClick={() => setMacroSymbols([])}
                  />
                </Box>
              )}

              <Button
                variant="contained"
                onClick={handleCalculateMacro}
                disabled={macroSymbols.length < 2 || loading}
                startIcon={
                  loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />
                }
              >
                Calculate Macro Correlation
              </Button>
            </Box>
          )}

          {/* Model Portfolios Tab — intra-portfolio correlation */}
          {tabIndex === 3 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Each model portfolio is treated as a synthetic weighted symbol (its daily
                return = the weight-sum of its holdings' returns), so the same correlation
                math applies portfolio-to-portfolio as it does stock-to-stock.
              </Typography>

              {/* ── Matrix controls ── */}
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <ToggleButtonGroup
                  value={portfolioMatrixTimeframe}
                  exclusive
                  size="small"
                  onChange={(_, v) => v && setPortfolioMatrixTimeframe(v)}
                >
                  {TIMEFRAMES.map((tf) => (
                    <ToggleButton key={tf.value} value={tf.value}>{tf.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <Button
                  variant="contained"
                  onClick={handleCalculatePortfolioMatrix}
                  disabled={portfolioMatrixLoading || allPortfolios.length < 2}
                  startIcon={portfolioMatrixLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                >
                  Calculate Matrix ({allPortfolios.length} portfolios)
                </Button>
              </Box>

              {portfolioError && (
                <Alert severity="warning" sx={{ mb: 2 }}>{portfolioError}</Alert>
              )}

              {portfolioMatrix && portfolioMatrix.portfolio_ids.length >= 2 && (
                <Paper sx={{ p: 2.5, mb: 3 }} elevation={1}>
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                    <Typography variant="h6">Portfolio Correlation Matrix</Typography>
                    <Chip
                      label={`Diversification score: ${portfolioMatrix.diversification_score ?? 'N/A'} / 100`}
                      color={
                        (portfolioMatrix.diversification_score ?? 0) >= 70 ? 'success'
                          : (portfolioMatrix.diversification_score ?? 0) >= 40 ? 'warning' : 'error'
                      }
                      size="small"
                    />
                    <Typography variant="body2" color="text.secondary">
                      avg pairwise correlation {portfolioMatrix.avg_pairwise_correlation ?? 'N/A'}
                      {' · '}{portfolioMatrix.timeframe} · {portfolioMatrix.days_used} days
                    </Typography>
                  </Box>
                  {portfolioMatrix.missing_portfolios.length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                      Excluded (insufficient price history): {portfolioMatrix.missing_portfolios.join(', ')}
                    </Typography>
                  )}
                  <CorrelationHeatmap
                    symbols={portfolioMatrix.portfolio_ids.map(String)}
                    symbolNames={Object.fromEntries(
                      Object.entries(portfolioMatrix.portfolio_names).map(([id, name]) => [id, name])
                    )}
                    matrix={portfolioMatrix.matrix}
                    onCellClick={(row, col) => {
                      if (row !== col) { setPairA(Number(row)); setPairB(Number(col)); }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Click any cell to load that pair into the rolling correlation chart below.
                  </Typography>
                </Paper>
              )}

              {/* ── Rolling (time-series) correlation for one pair ── */}
              <Paper sx={{ p: 2.5 }} elevation={1}>
                <Typography variant="h6" sx={{ mb: 2 }}>Rolling Correlation Over Time</Typography>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Portfolio A</InputLabel>
                    <Select value={pairA} label="Portfolio A" onChange={(e) => setPairA(e.target.value as number)}>
                      {allPortfolios.map((p) => (
                        <MenuItem key={p.id} value={p.id} disabled={p.id === pairB}>{p.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 220 }}>
                    <InputLabel>Portfolio B</InputLabel>
                    <Select value={pairB} label="Portfolio B" onChange={(e) => setPairB(e.target.value as number)}>
                      {allPortfolios.map((p) => (
                        <MenuItem key={p.id} value={p.id} disabled={p.id === pairA}>{p.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Window</InputLabel>
                    <Select value={lookbackPeriod} label="Window" onChange={(e) => setLookbackPeriod(e.target.value)}>
                      {TIMEFRAMES.map((tf) => (
                        <MenuItem key={tf.value} value={tf.value}>{tf.label} rolling</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small" type="number" label="History (days)" sx={{ width: 140 }}
                    value={historyDays}
                    onChange={(e) => setHistoryDays(Math.min(730, Math.max(30, Number(e.target.value) || 180)))}
                  />
                  <TextField
                    size="small" type="number" label="Step (days)" sx={{ width: 120 }}
                    value={stepDays}
                    onChange={(e) => setStepDays(Math.min(30, Math.max(1, Number(e.target.value) || 7)))}
                  />
                  <Button
                    variant="contained"
                    onClick={handleCalculatePairHistory}
                    disabled={pairHistoryLoading || !pairA || !pairB || pairA === pairB}
                    startIcon={pairHistoryLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                  >
                    Calculate
                  </Button>
                </Box>

                {pairHistory && pairHistory.history.length > 0 && (
                  <>
                    <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap' }}>
                      <Typography variant="body2">
                        <b>{pairHistory.portfolio_name_1}</b> vs <b>{pairHistory.portfolio_name_2}</b>
                      </Typography>
                      {pairHistory.statistics && (
                        <Typography variant="body2" color="text.secondary">
                          current {pairHistory.statistics.current.toFixed(3)} · avg {pairHistory.statistics.average.toFixed(3)}
                          {' · '}range [{pairHistory.statistics.min.toFixed(2)}, {pairHistory.statistics.max.toFixed(2)}]
                          {' · '}σ {pairHistory.statistics.std.toFixed(3)}
                        </Typography>
                      )}
                    </Box>
                    <ResponsiveContainer width="100%" height={280}>
                      <LineChart data={pairHistory.history}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis domain={[-1, 1]} tick={{ fontSize: 11 }} />
                        <RechartsTooltip formatter={(v: number) => v.toFixed(3)} />
                        <ReferenceLine y={0} stroke="#999" strokeDasharray="4 4" />
                        <Legend />
                        <Line
                          type="monotone" dataKey="correlation" name="Rolling correlation"
                          stroke="#ff8c00" dot={false} strokeWidth={2}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </>
                )}
                {pairHistory && pairHistory.history.length === 0 && !pairHistoryLoading && (
                  <Typography variant="body2" color="text.secondary">
                    No overlapping data for this pair/window — try a longer window or shorter history.
                  </Typography>
                )}
              </Paper>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Correlation Matrix Heatmap */}
      {correlationData && correlationData.symbols.length >= 2 && (
        <Paper sx={{ p: 3 }} elevation={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6">Correlation Matrix</Typography>
                {correlationData.as_of_date && (
                  <Chip
                    icon={<HistoryIcon />}
                    label={`As of ${correlationData.as_of_date}`}
                    size="small"
                    color="info"
                    variant="outlined"
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                {correlationData.timeframe} period | {correlationData.metadata.data_points} data points
                {correlationData.metadata.date_range_start && correlationData.metadata.date_range_end && (
                  <> | {correlationData.metadata.date_range_start} to {correlationData.metadata.date_range_end}</>
                )}
              </Typography>
            </Box>
          </Box>

          <CorrelationHeatmap
            symbols={correlationData.symbols}
            symbolNames={correlationData.symbol_names}
            matrix={correlationData.matrix}
          />

          {/* Data Coverage Info */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Data Coverage
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(correlationData.data_coverage).map(([symbol, days]) => (
                <Chip
                  key={symbol}
                  label={`${symbol}: ${days} days`}
                  size="small"
                  color={days >= correlationData.days_used * 0.8 ? 'success' : 'warning'}
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>

          {/* Correlation Comparison Table (when viewing historical data) */}
          {correlationData.as_of_date && correlationData.comparison && (
            <CorrelationComparisonTable
              comparison={correlationData.comparison}
              symbolNames={correlationData.symbol_names}
              asOfDate={correlationData.as_of_date}
            />
          )}
        </Paper>
      )}
    </Box>
  );
};

export default CorrelationStudies;
